import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorMessage, Spinner } from '../components/common';
import { ResultNotice } from '../components/ResultNotice';
import { formatDate } from '../lib/format';
import { useApi, useDocumentTitle } from '../hooks/useApi';
import { usePaymentReturn } from '../hooks/usePaymentReturn';
import { api, ApiError } from '../lib/api';
import type { MySubscription } from '../lib/types';
import './account.css';

const STATUS_LABEL: Record<string, string> = {
  active: 'Activa',
  pastdue: 'Pago pendiente',
  canceled: 'Cancelada',
  unpaid: 'Impagada',
  none: 'Sin suscripción',
};

export function SubscriptionPage() {
  useDocumentTitle('Mi suscripción');

  const { data, error, loading, reload } = useApi<MySubscription>('/me/subscription');
  const justPaid = usePaymentReturn();
  const [noticeOpen, setNoticeOpen] = useState(justPaid);
  const [attempts, setAttempts] = useState(0);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Stripe avisa a la API unos segundos después de que el alumno vuelva: hasta entonces la
  // suscripción no existe aquí. Se mira cada 3 s, hasta 30; el vitalicio no crea ninguna.
  const waiting = justPaid && !data?.planCode && attempts < 10;

  useEffect(() => {
    if (!waiting) {
      return;
    }

    const timer = window.setTimeout(() => {
      setAttempts((value) => value + 1);
      reload();
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [waiting, attempts, reload]);

  async function openPortal() {
    setBusy(true);
    setPortalError(null);

    try {
      const { url } = await api.get<{ url: string }>('/billing/portal');
      window.location.assign(url);
    } catch (caught) {
      setPortalError(
        caught instanceof ApiError ? caught.message : 'No hemos podido abrir el portal de pagos.',
      );
      setBusy(false);
    }
  }

  if (loading) {
    return <Spinner />;
  }

  if (error || !data) {
    return (
      <div className="container section">
        <ErrorMessage>No hemos podido cargar tu suscripción.</ErrorMessage>
      </div>
    );
  }

  // Stripe la mantiene activa hasta el final del periodo pagado, pero para el alumno ya está
  // cancelada: es lo que dice el aviso, y el estado no puede decir otra cosa.
  const status = data.cancelAtPeriodEnd && data.status === 'active' ? 'canceled' : data.status;

  return (
    <div className="container section account">
      <header className="section-header">
        <h1>Mi suscripción</h1>
        <p className="muted">
          <Link to="/cuenta">← Volver a mi cuenta</Link>
        </p>
      </header>

      {noticeOpen && (
        <ResultNotice
          title="Pago completado"
          celebrate
          onClose={() => setNoticeOpen(false)}
          actions={
            <Link to="/cuenta/facturas" className="btn btn--ghost btn--sm">
              Ver mis facturas
            </Link>
          }
        >
          <p>
            {data.planCode ? (
              <>
                Tu plan <strong>{data.planName}</strong> ya está activo.
              </>
            ) : waiting ? (
              'Estamos activando lo que has contratado: aparecerá aquí en unos segundos.'
            ) : (
              'Ya tienes acceso a lo que has contratado.'
            )}{' '}
            Te enviamos la factura por email en unos minutos.
          </p>
        </ResultNotice>
      )}

      {portalError && <ErrorMessage>{portalError}</ErrorMessage>}

      {data.status === 'pastdue' && (
        <div className="alert alert--error" role="alert">
          Tu último pago no se ha completado. Mantienes el acceso unos días mientras lo
          solucionas; actualiza el método de pago desde el portal.
        </div>
      )}

      {data.cancelAtPeriodEnd && (
        <div className="alert alert--info">
          Tu suscripción está cancelada y no se renovará. Conservas el acceso hasta el{' '}
          {formatDate(data.currentPeriodEnd)}.
        </div>
      )}

      <dl className="subscription__summary">
        <div>
          <dt>Plan</dt>
          <dd>{data.planName ?? 'Ninguno'}</dd>
        </div>
        <div>
          <dt>Estado</dt>
          <dd>
            <span className={`status-pill status-pill--${status}`}>
              {STATUS_LABEL[status] ?? status}
            </span>
          </dd>
        </div>
        {data.planCode && (
          <>
            <div>
              <dt>{data.cancelAtPeriodEnd ? 'Acceso hasta' : 'Próxima renovación'}</dt>
              <dd>{formatDate(data.currentPeriodEnd)}</dd>
            </div>
            <div>
              <dt>Packs sectoriales</dt>
              <dd>{data.includesPacks ? 'Incluidos' : 'No incluidos'}</dd>
            </div>
          </>
        )}
      </dl>

      <div className="row">
        {data.planCode ? (
          <button type="button" className="btn btn--primary" onClick={() => void openPortal()} disabled={busy}>
            {busy ? 'Abriendo…' : 'Gestionar pago y suscripción'}
          </button>
        ) : (
          <Link to="/precios" className="btn btn--accent">
            Elegir un plan
          </Link>
        )}

        <Link to="/cuenta/facturas" className="btn btn--ghost">
          Ver facturas
        </Link>
      </div>

      <section>
        <h2>En propiedad</h2>
        <p className="muted">
          Cursos y packs que son tuyos para siempre, comprados por separado o con el programa
          vitalicio. No dependen de la suscripción: siguen siendo tuyos aunque la canceles.
        </p>

        {data.ownedProducts.length === 0 ? (
          <p className="muted">Ninguno todavía.</p>
        ) : (
          <ul>
            {data.ownedProducts.map((product) => (
              <li key={product.slug}>
                {product.type === 'program' ? (
                  product.title
                ) : (
                  <Link to={`/${product.type === 'pack' ? 'pack' : 'curso'}/${product.slug}`}>{product.title}</Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
